import { useQuery } from "@tanstack/react-query";
import { listChapters, type Chapter } from "../api";

/**
 * useReaderChapters is the book's published chapter list, under the same key
 * the reader's book page reads it with, so moving between the two costs no
 * second request. Kept in the server's order: that order is the book's.
 */
export function useReaderChapters(bookID: string | undefined) {
  return useQuery({
    queryKey: ["chapters", bookID, "reader"],
    queryFn: () => listChapters(bookID!),
    enabled: !!bookID,
    retry: false,
  });
}

/** The chapters either side of one, as the list orders them. */
export interface Neighbours {
  previous?: Chapter;
  next?: Chapter;
  /** 1-based, or 0 when the chapter is not in the list. */
  number: number;
}

/**
 * neighbours finds a chapter's previous and next in the reader's list. A
 * chapter the list does not hold — a stale cache, a link to one since
 * unpublished — has neither, rather than pointing at the book's first.
 */
export function neighbours(items: Chapter[], chapterID: string): Neighbours {
  const i = items.findIndex((c) => c.id === chapterID);
  if (i < 0) return { number: 0 };
  return { previous: items[i - 1], next: items[i + 1], number: i + 1 };
}
